import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RowData } from 'components/Table/types/rowData';
import deepMerge from 'deepmerge';
import { ApplicationState } from 'redux/applicationState';
import {
  deleteChildren,
  deleteRow,
  editRow,
  hasChildren,
  updateCreditLimitConsumed,
  updateRow,
} from 'redux/reducers/utils';
import {
  Account,
  AccountsGroup,
  AccountToSortCreditLimits,
  AccountToSortSymbolLimits,
  AllOCC,
  AssetClassType,
  CompanyWithMpids,
  RemoveSymbolLimitsRequest,
  RiskAccount,
  RiskClearingAccount,
  RiskCreditLimitsAccount,
  RiskCreditLimitsMatrix,
  RiskMatrix,
  SetAccountWithChildren,
  SymbolLimits,
  SymbolTableRow,
  UpdateOrCreateEntryRequest,
} from 'types/risk';

export interface AssetClassRequest<T> {
  readonly type: AssetClassType;
  readonly value: T;
}

export interface RiskEngine {
  readonly clientid: string;
  readonly sessionid: string;
}

export type RiskSession = Omit<RiskEngine, 'clientid'>;

export interface RiskState {
  clientID: string;
  sessionID: string;
  matrix: RiskMatrix | null;
  originalMatrix: RiskMatrix | null;
  fetchingRiskMatrix: boolean;
  savingRiskMatrix: boolean;
  fetchingRiskSessions: boolean;
  fetchingRiskEngines: boolean;
  engines: readonly RiskEngine[];
  sessions: readonly RiskSession[];
  savingRiskAccount: boolean;
  deletingRiskAccount: boolean;
  successMessage: string | null;
  errorMessage: string | null;
  fetchingRiskAccounts: boolean;
  riskAccounts: RiskAccount | null;
  riskCreditLimitsMatrix: RiskCreditLimitsMatrix | null;
  originalRiskCreditLimitsMatrix: RiskCreditLimitsMatrix | null;
  fetchingRiskCreditLimitsMatrix: boolean;
  savingRiskCreditLimitsMatrix: boolean;
  symbolLimitsRows: AccountToSortSymbolLimits;
  originalSymbolLimitsRows: AccountToSortSymbolLimits;
  creditLimitsRows: AccountToSortCreditLimits;
  originalCreditLimitsRows: AccountToSortCreditLimits;
  isDeletingRiskAccount: boolean;
  allOCC: AllOCC[] | null;
  fetchingAllOCC: boolean;
  companiesWithMpids: CompanyWithMpids[] | null;
  fetchingCompaniesWithMpids: boolean;
  riskClearingAccounts: RiskClearingAccount[] | null;
  fetchingRiskClearingAccounts: boolean;
}

const initialState: RiskState = {
  clientID: '',
  sessionID: '',
  matrix: null,
  originalMatrix: null,
  fetchingRiskMatrix: false,
  savingRiskMatrix: false,
  fetchingRiskSessions: false,
  fetchingRiskEngines: false,
  engines: [],
  sessions: [],
  savingRiskAccount: false,
  deletingRiskAccount: false,
  successMessage: null,
  errorMessage: null,
  fetchingRiskAccounts: false,
  riskAccounts: null,
  riskCreditLimitsMatrix: null,
  originalRiskCreditLimitsMatrix: null,
  fetchingRiskCreditLimitsMatrix: false,
  savingRiskCreditLimitsMatrix: false,
  symbolLimitsRows: {
    equities: null,
    options: null,
  },
  originalSymbolLimitsRows: {
    equities: null,
    options: null,
  },
  creditLimitsRows: null,
  originalCreditLimitsRows: null,
  isDeletingRiskAccount: false,
  allOCC: null,
  fetchingAllOCC: false,
  companiesWithMpids: null,
  fetchingCompaniesWithMpids: false,
  riskClearingAccounts: null,
  fetchingRiskClearingAccounts: false,
};

const slice = createSlice({
  name: 'risk',
  initialState: initialState,
  reducers: {
    setClient: (state: RiskState, action: PayloadAction<string>): void => {
      state.clientID = action.payload;
      state.matrix = null;
      state.sessions = [];
    },
    setSessionID: (state: RiskState, action: PayloadAction<string>): void => {
      state.sessionID = action.payload;
    },
    fetchingRiskMatrixStarted: (state: RiskState): void => {
      state.fetchingRiskMatrix = true;
      state.matrix = null;
    },
    setRiskMatrix: (state: RiskState, action: PayloadAction<RiskMatrix>): void => {
      state.matrix = action.payload;
      state.originalMatrix = state.matrix;
    },
    updateSymbolLimits: (
      state: RiskState,
      action: PayloadAction<AssetClassRequest<UpdateOrCreateEntryRequest>>
    ): void => {
      const { payload } = action;
      const { matrix } = state;

      const { account, symbolLimits } = payload.value;

      const updatedMatrix = {
        [payload.type]: {
          accounts: [{ account: account, symbolLimits: [symbolLimits] }],
        },
      };

      state.matrix = deepMerge(matrix, updatedMatrix);
    },
    removeSymbolLimits: (
      state: RiskState,
      action: PayloadAction<AssetClassRequest<RemoveSymbolLimitsRequest>>
    ): void => {
      const { payload } = action;
      const { matrix } = state;

      const group: AccountsGroup = matrix[payload.type];
      const { accounts } = group;
      const request = payload.value;
      const matchingAccount = accounts.find(
        (account: Account): boolean => account.account === request.account
      );

      if (matchingAccount) {
        const { accounts } = group;
        state.matrix = {
          ...matrix,
          [payload.type]: {
            ...group,
            accounts: accounts.map((account: Account): Account => {
              if (account.account === request.account) {
                const { symbolLimits } = account;

                return {
                  account: request.account,
                  parent_account: account.parent_account,
                  symbolLimits: symbolLimits.filter(
                    (currentEntry: SymbolLimits): boolean => currentEntry.symbol !== request.symbol
                  ),
                };
              } else {
                return account;
              }
            }),
          },
        };
      } else {
        throw new Error('missing account, cannot remove this item');
      }
    },
    replaceRiskMatrix: (state: RiskState, action: PayloadAction<RiskMatrix>): void => {
      state.matrix = action.payload;
    },
    fetchingRiskMatrixCompleted: (state: RiskState): void => {
      state.fetchingRiskMatrix = false;
    },
    savingRiskMatrixStarted: (state: RiskState): void => {
      state.savingRiskMatrix = true;
    },
    savingRiskMatrixSucceeded: (state: RiskState): void => {
      state.originalMatrix = state.matrix;
      state.successMessage = 'Risk matrix saved successfully';
    },
    restoreOriginalRiskMatrix: (state: RiskState): void => {
      state.matrix = state.originalMatrix;
    },
    savingRiskMatrixFailed: (state: RiskState, action: PayloadAction<string>): void => {
      state.errorMessage = action.payload;
    },
    savingRiskMatrixCompleted: (state: RiskState): void => {
      state.savingRiskMatrix = false;
    },
    fetchingRiskSessionsStarted: (state: RiskState): void => {
      state.fetchingRiskSessions = true;
    },
    setRiskSessions: (state: RiskState, action: PayloadAction<readonly RiskSession[]>): void => {
      state.sessions = action.payload;
    },
    fetchingRiskSessionsCompleted: (state: RiskState): void => {
      state.fetchingRiskSessions = false;
    },
    fetchingRiskEnginesStarted: (state: RiskState): void => {
      state.fetchingRiskEngines = true;
    },
    setRiskEngines: (state: RiskState, action: PayloadAction<readonly RiskEngine[]>): void => {
      state.engines = action.payload;
    },
    fetchingRiskEnginesCompleted: (state: RiskState): void => {
      state.fetchingRiskEngines = false;
    },
    setAccountIDTag: (state: RiskState, action: PayloadAction<AssetClassRequest<number>>): void => {
      const { payload } = action;
      const { matrix } = state;

      const group: AccountsGroup = matrix[payload.type];

      state.matrix = {
        ...state.matrix,
        [payload.type]: { ...group, accounttag: payload.value },
      };
    },
    resetSuccessMessage: (state: RiskState): void => {
      state.successMessage = null;
    },
    resetErrorMessage: (state: RiskState): void => {
      state.errorMessage = null;
    },
    creatingRiskAccountSucceeded: (state: RiskState): void => {
      state.successMessage = 'Risk Account created successfully';
    },
    creatingRiskAccountFailed: (state: RiskState, action: PayloadAction<string>): void => {
      state.errorMessage = action.payload;
    },
    creatingRiskAccountStarted: (state: RiskState): void => {
      state.savingRiskAccount = true;
    },
    creatingRiskAccountCompleted: (state: RiskState): void => {
      state.savingRiskAccount = false;
    },
    deletingRiskAccountStarted: (state: RiskState): void => {
      state.deletingRiskAccount = true;
    },
    deletingRiskAccountCompleted: (state: RiskState): void => {
      state.deletingRiskAccount = false;
    },
    deletingRiskAccountFailed: (state: RiskState, action: PayloadAction<string>): void => {
      state.errorMessage = action.payload;
    },
    deletingRiskAccountSucceeded: (state: RiskState): void => {
      state.successMessage = 'Risk Account deleted successfully';
    },
    fetchingRiskAccountsStarted: (state: RiskState): void => {
      state.fetchingRiskAccounts = true;
    },
    fetchingRiskAccountsCompleted: (state: RiskState): void => {
      state.fetchingRiskAccounts = false;
    },
    setRiskAccounts: (state: RiskState, action: PayloadAction<RiskAccount>): void => {
      state.riskAccounts = action.payload;
    },
    fetchingRiskCreditLimitsMatrixStarted: (state: RiskState): void => {
      state.fetchingRiskCreditLimitsMatrix = true;
    },
    fetchingRiskCreditLimitsMatrixCompleted: (state: RiskState): void => {
      state.fetchingRiskCreditLimitsMatrix = false;
    },
    setRiskCreditLimitsMatrix: (
      state: RiskState,
      action: PayloadAction<RiskCreditLimitsMatrix>
    ): void => {
      state.riskCreditLimitsMatrix = action.payload;
      state.originalRiskCreditLimitsMatrix = state.riskCreditLimitsMatrix;
    },
    updateCreditLimits: (
      state: RiskState,
      action: PayloadAction<RiskCreditLimitsAccount>
    ): void => {
      const { payload } = action;
      const { riskCreditLimitsMatrix } = state;

      const updatedMatrix = {
        ...riskCreditLimitsMatrix,
        accounts: [...riskCreditLimitsMatrix.accounts, payload],
      };

      state.riskCreditLimitsMatrix = updatedMatrix;
    },
    removeCreditLimits: (state: RiskState, action: PayloadAction<string>): void => {
      const { payload } = action;
      const { riskCreditLimitsMatrix } = state;

      const updatedMatrix = {
        ...riskCreditLimitsMatrix,
        accounts: riskCreditLimitsMatrix.accounts.filter((account) => account.account !== payload),
      };

      state.riskCreditLimitsMatrix = updatedMatrix;
    },
    restoreOriginalCreditLimitsMatrix: (state: RiskState): void => {
      state.riskCreditLimitsMatrix = state.originalRiskCreditLimitsMatrix;
    },
    savingRiskCreditLimitsMatrixSucceeded: (state: RiskState): void => {
      state.originalRiskCreditLimitsMatrix = state.riskCreditLimitsMatrix;
      state.successMessage = 'Risk matrix saved successfully';
    },
    savingRiskCreditLimitsMatrixStarted: (state: RiskState): void => {
      state.savingRiskCreditLimitsMatrix = true;
    },
    savingRiskCreditLimitsMatrixFailed: (state: RiskState, action: PayloadAction<string>): void => {
      state.errorMessage = action.payload;
    },
    savingRiskCreditLimitsMatrixCompleted: (state: RiskState): void => {
      state.savingRiskCreditLimitsMatrix = false;
    },
    setSymbolLimitsRows: (
      state: RiskState,
      action: PayloadAction<AssetClassRequest<SetAccountWithChildren>>
    ): void => {
      const { type, value } = action.payload;
      state.symbolLimitsRows[type] = value.symbolLimits;
      state.originalSymbolLimitsRows[type] = value.symbolLimits;
    },
    updateSymbolLimitsRows: (
      state: RiskState,
      action: PayloadAction<AssetClassRequest<SetAccountWithChildren>>
    ): void => {
      const { type, value } = action.payload;
      const rowKey = Object.keys(value.symbolLimits)[0];
      const deepClone = JSON.parse(JSON.stringify(state.symbolLimitsRows[type]));
      const hasAnyChildren: boolean = hasChildren(deepClone as any, value.symbolLimits[rowKey].id);

      const updatedRows = hasAnyChildren
        ? deleteChildren(deepClone, rowKey)
        : updateRow(deepClone, rowKey, type);
      state.symbolLimitsRows[type] = updatedRows as Record<string, RowData<SymbolTableRow>>;
    },
    deleteSymbolLimitsRows: (
      state: RiskState,
      action: PayloadAction<AssetClassRequest<SetAccountWithChildren>>
    ): void => {
      const { type, value } = action.payload;
      const rowKey = Object.keys(value.symbolLimits)[0];
      const deepClone = JSON.parse(JSON.stringify(state.symbolLimitsRows[type]));

      const updatedRows = deleteRow(deepClone, rowKey);
      state.symbolLimitsRows[type] = updatedRows as Record<string, RowData<SymbolTableRow>>;
    },
    editSymbolLimitsRows: (
      state: RiskState,
      action: PayloadAction<AssetClassRequest<SetAccountWithChildren>>
    ): void => {
      const { type, value } = action.payload;
      const rowKey = Object.keys(value.symbolLimits)[0];
      const deepClone = JSON.parse(JSON.stringify(state.symbolLimitsRows[type]));

      const updatedRows = editRow(deepClone, rowKey, value.symbolLimits[rowKey].data);
      state.symbolLimitsRows[type] = updatedRows as Record<string, RowData<SymbolTableRow>>;
    },
    restoreOriginalSymbolLimitsRows: (state: RiskState): void => {
      state.symbolLimitsRows = state.originalSymbolLimitsRows;
    },

    //Credit limits
    setCreditLimitsRows: (
      state: RiskState,
      action: PayloadAction<AccountToSortCreditLimits>
    ): void => {
      state.creditLimitsRows = action.payload;
      state.originalCreditLimitsRows = action.payload;
    },
    updateCreditLimitsRows: (
      state: RiskState,
      action: PayloadAction<AccountToSortCreditLimits>
    ): void => {
      const rowKey = Object.keys(action.payload)[0];
      const deepClone = JSON.parse(JSON.stringify(state.creditLimitsRows));
      const hasAnyChildren: boolean = hasChildren(deepClone as any, action.payload[rowKey].id);

      const updatedRows = hasAnyChildren
        ? deleteChildren(deepClone, rowKey)
        : updateRow(deepClone, rowKey);

      state.creditLimitsRows = updatedRows as unknown as AccountToSortCreditLimits;
    },
    deleteCreditLimitsRows: (
      state: RiskState,
      action: PayloadAction<AccountToSortCreditLimits>
    ): void => {
      const rowKey = Object.keys(action.payload)[0];
      const deepClone = JSON.parse(JSON.stringify(state.creditLimitsRows));

      const updatedRows = deleteRow(deepClone, rowKey);
      state.creditLimitsRows = updatedRows as unknown as AccountToSortCreditLimits;
    },
    editCreditLimitsRows: (
      state: RiskState,
      action: PayloadAction<AccountToSortCreditLimits>
    ): void => {
      const rowKey = Object.keys(action.payload)[0];
      const deepClone = JSON.parse(JSON.stringify(state.creditLimitsRows));

      const updatedRows = editRow(deepClone, rowKey, action.payload[rowKey].data as any);
      state.creditLimitsRows = updatedRows as unknown as AccountToSortCreditLimits;
    },
    restoreOriginalCreditLimitsRows: (state: RiskState): void => {
      state.creditLimitsRows = state.originalCreditLimitsRows;
    },
    updateCreditLimitsSignalR: (
      state: RiskState,
      action: PayloadAction<{ account: string; consumed: number }>
    ): void => {
      const deepClone = JSON.parse(JSON.stringify(state.creditLimitsRows));

      const updatedRows = updateCreditLimitConsumed(
        deepClone,
        action.payload.account,
        action.payload.consumed
      );
      state.creditLimitsRows = updatedRows as unknown as AccountToSortCreditLimits;
    },
    setIsDeletingRiskAccount: (state: RiskState, action: PayloadAction<boolean>): void => {
      state.isDeletingRiskAccount = action.payload;
    },
    setAllOCC: (state: RiskState, action: PayloadAction<AllOCC[]>): void => {
      state.allOCC = action.payload;
    },
    fetchingAllOCCStarted: (state: RiskState): void => {
      state.fetchingAllOCC = true;
    },
    fetchingAllOCCCompleted: (state: RiskState): void => {
      state.fetchingAllOCC = false;
    },
    setCompaniesWithMpids: (state: RiskState, action: PayloadAction<CompanyWithMpids[]>): void => {
      state.companiesWithMpids = action.payload;
    },
    fetchingCompaniesWithMpidsStarted: (state: RiskState): void => {
      state.fetchingCompaniesWithMpids = true;
    },
    fetchingCompaniesWithMpidsCompleted: (state: RiskState): void => {
      state.fetchingCompaniesWithMpids = false;
    },
    fetchingRiskClearingAccountsStarted: (state: RiskState): void => {
      state.fetchingRiskClearingAccounts = true;
    },
    fetchingRiskClearingAccountsCompleted: (state: RiskState): void => {
      state.fetchingRiskClearingAccounts = false;
    },
    setRiskClearingAccounts: (state: RiskState, action: PayloadAction<any>): void => {
      state.riskClearingAccounts = action.payload;
    },
  },
});

export const {
  setClient,
  setSessionID,
  setRiskMatrix,
  replaceRiskMatrix,
  fetchingRiskMatrixStarted,
  fetchingRiskMatrixCompleted,
  savingRiskMatrixStarted,
  savingRiskMatrixCompleted,
  savingRiskMatrixSucceeded,
  savingRiskMatrixFailed,
  fetchingRiskSessionsStarted,
  setRiskSessions,
  fetchingRiskSessionsCompleted,
  fetchingRiskEnginesStarted,
  setRiskEngines,
  fetchingRiskEnginesCompleted,
  setAccountIDTag,
  resetErrorMessage,
  resetSuccessMessage,
  updateSymbolLimits,
  removeSymbolLimits,
  restoreOriginalRiskMatrix,
  creatingRiskAccountSucceeded,
  creatingRiskAccountFailed,
  creatingRiskAccountStarted,
  creatingRiskAccountCompleted,
  deletingRiskAccountStarted,
  deletingRiskAccountCompleted,
  deletingRiskAccountFailed,
  deletingRiskAccountSucceeded,
  fetchingRiskAccountsStarted,
  fetchingRiskAccountsCompleted,
  setRiskAccounts,
  fetchingRiskCreditLimitsMatrixStarted,
  fetchingRiskCreditLimitsMatrixCompleted,
  setRiskCreditLimitsMatrix,
  updateCreditLimits,
  removeCreditLimits,
  restoreOriginalCreditLimitsMatrix,
  savingRiskCreditLimitsMatrixSucceeded,
  savingRiskCreditLimitsMatrixStarted,
  savingRiskCreditLimitsMatrixFailed,
  savingRiskCreditLimitsMatrixCompleted,
  setSymbolLimitsRows,
  updateSymbolLimitsRows,
  deleteSymbolLimitsRows,
  editSymbolLimitsRows,
  restoreOriginalSymbolLimitsRows,
  setCreditLimitsRows,
  updateCreditLimitsRows,
  deleteCreditLimitsRows,
  editCreditLimitsRows,
  restoreOriginalCreditLimitsRows,
  updateCreditLimitsSignalR,
  setIsDeletingRiskAccount,
  setAllOCC,
  fetchingAllOCCStarted,
  fetchingAllOCCCompleted,
  setCompaniesWithMpids,
  fetchingCompaniesWithMpidsStarted,
  fetchingCompaniesWithMpidsCompleted,
  fetchingRiskClearingAccountsStarted,
  fetchingRiskClearingAccountsCompleted,
  setRiskClearingAccounts,
} = slice.actions;

export const riskSelector = (state: ApplicationState): RiskState => state.risk;

export default slice.reducer;

export const createAssetClassRequest = <T>(
  type: AssetClassType,
  value: T
): AssetClassRequest<T> => {
  return { type: type, value: value };
};
